import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PaymentOption, PaymentOptionsError, PaymentOptionsStatus } from '../../model'
import { PaymentType } from '@afterpay/types'
import { PURGE } from 'redux-persist'
import { createDocuments, getPaymentOptions, updateCheckout, updatePaymentOption, updatePaymentOptions } from '../checkout/actions'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'

export interface ConsumerLendingState {
  selectedPaymentType?: PaymentType
  selectedPaymentOptionId?: number
  isAutopayEnabled?: boolean
  paymentOptions: PaymentOption[]
  paymentOptionsStatus: PaymentOptionsStatus
  loanAgreementDocumentId?: string
  paymentAuthorisationDocumentId?: string
  creditScoreDisclosureDocumentId?: string
  showEfaModal: boolean
  error?: PaymentOptionsError
}

export const consumerLendingInitialState: ConsumerLendingState = {
  selectedPaymentType: undefined,
  selectedPaymentOptionId: undefined,
  isAutopayEnabled: true,
  paymentOptions: [],
  paymentOptionsStatus: PaymentOptionsStatus.Initial,
  loanAgreementDocumentId: undefined,
  paymentAuthorisationDocumentId: undefined,
  showEfaModal: false,
  error: undefined,
}

const slice = createSlice({
  name: 'consumerLending',
  initialState: consumerLendingInitialState,
  reducers: {
    setSelectedPaymentType(state: ConsumerLendingState, action: PayloadAction<PaymentType>) {
      state.selectedPaymentType = action.payload
    },
    setSelectedPaymentOptionId(state: ConsumerLendingState, action: PayloadAction<number>) {
      state.selectedPaymentOptionId = action.payload
    },
    setIsUpdatingCLAutopayEnabled(state: ConsumerLendingState, action: PayloadAction<boolean>) {
      state.isAutopayEnabled = action.payload
    },
    setPaymentOptionsError(state: ConsumerLendingState, action: PayloadAction<PaymentOptionsError>) {
      state.error = action.payload
      // its assumed that if a payment options error happens, the customer will be forced to use PBI.
      state.selectedPaymentType = PaymentType.PBI
    },
    setShowEfaModal(state: ConsumerLendingState, action: PayloadAction<boolean>) {
      state.showEfaModal = action.payload
    },
    clearPaymentOptionsError(state: ConsumerLendingState) {
      state.error = undefined
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getPaymentOptions.fulfilled, (state, action) => {
        state.paymentOptions = action.payload
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
      })
      .addCase(getPaymentOptions.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetching
      })
      .addCase(getPaymentOptions.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Error
      })
      .addCase(updatePaymentOptions.fulfilled, (state, action) => {
        state.paymentOptions = action.payload
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
      })
      .addCase(updatePaymentOptions.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetching
      })
      .addCase(updatePaymentOptions.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Error
      })
      .addCase(updatePaymentOption.fulfilled, (state, action) => {
        state.selectedPaymentType = action.payload.paymentOption.paymentType
        state.selectedPaymentOptionId = action.payload.paymentOption.id
        state.paymentOptionsStatus = PaymentOptionsStatus.Fetched
      })
      .addCase(updatePaymentOption.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Updating
      })
      .addCase(updatePaymentOption.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.UpdateError
      })
      .addCase(createDocuments.fulfilled, (state, action) => {
        const response = action.payload
        state.loanAgreementDocumentId = response.LOAN_AGREEMENT
        state.paymentAuthorisationDocumentId = response.PAYMENT_AUTHORISATION
        state.creditScoreDisclosureDocumentId = response.CREDIT_SCORE_DISCLOSURE
      })
      .addCase(createDocuments.pending, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.Updating
      })
      .addCase(createDocuments.rejected, (state) => {
        state.paymentOptionsStatus = PaymentOptionsStatus.UpdateError
      })
      .addCase(updateCheckout.fulfilled, (state, action) => {
        const { paymentOptions } = action.payload
        if (valueIsNotNullOrUndefined(paymentOptions)) {
          state.paymentOptions = paymentOptions
          // TODO: Add selected payment options here once payment option is removed.
        }
      })
      .addCase(PURGE, () => consumerLendingInitialState)
  },
})

export const {
  setSelectedPaymentType,
  setSelectedPaymentOptionId,
  setIsUpdatingCLAutopayEnabled,
  setShowEfaModal,
  setPaymentOptionsError,
  clearPaymentOptionsError,
} = slice.actions

export default slice.reducer
